import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  listAccounts, createAccount, updateAccount, deleteAccount,
  listCostCenters, createCostCenter, updateCostCenter, deleteCostCenter,
  listBankAccounts, createBankAccount, updateBankAccount, deleteBankAccount
} from '../../../../api/openFinanceTransactions';
import { C, Btn, Overlay } from '../components/shared';
import { _Input } from '../../../../_components/Input';
import AccountSelect from '../components/shared';

// ==========================================
// Componente de Tabela Genérica para CRUD
// ==========================================
function CrudTable({ title, icon, columns, data, onEdit, onDelete, onCreate, loading }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, marginBottom: 16, overflow: 'hidden' }}>
      <div 
        onClick={() => setOpen(!open)}
        style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', background: open ? '#F8FAFC' : '#fff', transition: 'background .2s' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: C.blueBg, color: C.blue, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className={`fas fa-${icon}`} />
          </div>
          <h3 style={{ margin: 0, fontSize: 15, color: C.text }}>{title}</h3>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Btn small onClick={(e) => { e.stopPropagation(); onCreate(); }}>
            <i className="fas fa-plus"/> Novo
          </Btn>
          <i className={`fas fa-chevron-${open ? 'up' : 'down'}`} style={{ color: C.soft }} />
        </div>
      </div>
      
      {open && (
        <div style={{ borderTop: `1px solid ${C.border}` }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#F8FAFC', borderBottom: `1px solid ${C.border}` }}>
                {columns.map((c, i) => <th key={i} style={{ padding: '10px 16px', textAlign: 'left', color: C.soft, fontWeight: 600 }}>{c.label}</th>)}
                <th style={{ padding: '10px 16px', textAlign: 'right', color: C.soft, fontWeight: 600 }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={columns.length + 1} style={{ padding: 20, textAlign: 'center', color: C.soft }}>Carregando...</td></tr>}
              {!loading && data.length === 0 && <tr><td colSpan={columns.length + 1} style={{ padding: 20, textAlign: 'center', color: C.soft }}>Nenhum registro encontrado.</td></tr>}
              {data.map((row, idx) => (
                <tr key={row.id} style={{ borderBottom: `1px solid ${C.border}`, background: idx % 2 === 0 ? '#fff' : '#FAFBFC' }}>
                  {columns.map((c, i) => (
                    <td key={i} style={{ padding: '10px 16px', color: C.text }}>
                      {c.render ? c.render(row) : row[c.key]}
                    </td>
                  ))}
                  <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                    <Btn small outline color={C.blue} onClick={() => onEdit(row)} style={{ marginRight: 8 }}><i className="fas fa-pen"/></Btn>
                    <Btn small outline color={C.red} onClick={() => onDelete(row)}><i className="fas fa-trash"/></Btn>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


export default function Settings() {
  // States
  const [accounts, setAccounts] = useState([]);
  const [centers, setCenters] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modals
  const [modalType, setModalType] = useState(null); // 'account', 'center', 'bank', or null
  const [current, setCurrent] = useState(null); // null for create, object for edit
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Delete Confirm
  const [delTarget, setDelTarget] = useState(null); // { type, id, name }

  async function loadAll() {
    setLoading(true);
    try {
      const [acc, cc, bk] = await Promise.all([
        listAccounts().catch(()=>[]),
        listCostCenters().catch(()=>[]),
        listBankAccounts().catch(()=>[])
      ]);
      setAccounts(acc);
      setCenters(cc);
      setBanks(bk);
    } catch(e) {}
    setLoading(false);
  }

  useEffect(() => { loadAll(); }, []);

  const handleChange = e => {
    const target = e.target || e;
    setForm(f => ({ ...f, [target.name]: target.value }));
  };

  // =============== SAVE ===============
  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    try {
      if (modalType === 'account') {
        const payload = {
          name: form.name,
          code: form.code || null,
          type: form.type || null,
          description: form.description || null,
          parent_id: form.parent_id || null,
        };
        current ? await updateAccount(current.id, payload) : await createAccount(payload);
      } else if (modalType === 'center') {
        const payload = { name: form.name, code: form.code || null };
        current ? await updateCostCenter(current.id, payload) : await createCostCenter(payload);
      } else if (modalType === 'bank') {
        const payload = { 
          name: form.name, 
          account_type: form.account_type || 'checking',
          initial_balance: parseFloat(form.initial_balance || 0)
        };
        current ? await updateBankAccount(current.id, payload) : await createBankAccount(payload);
      }
      toast.success('Salvo com sucesso!');
      setModalType(null);
      loadAll();
    } catch (err) {
      toast.error('Erro ao salvar.');
    }
    setSaving(false);
  }

  // =============== DELETE ===============
  async function handleDeleteConfirm() {
    setSaving(true);
    try {
      if (delTarget.type === 'account') await deleteAccount(delTarget.id);
      if (delTarget.type === 'center') await deleteCostCenter(delTarget.id);
      if (delTarget.type === 'bank') await deleteBankAccount(delTarget.id);
      toast.success('Excluído com sucesso!');
      setDelTarget(null);
      loadAll();
    } catch(e) {
      toast.error('Erro ao excluir. Verifique se está em uso.');
    }
    setSaving(false);
  }

  // Helper render modals
  const renderForm = () => {
    if (modalType === 'account') {
      return (
        <div style={{ display: 'grid', gap: 12 }}>
          <_Input label="Nome da Categoria*" name="name" value={form.name} onChange={handleChange} />
          <_Input label="Categoria Pai" name="parent_id" type="select" value={form.parent_id} onChange={handleChange} 
            options={[{label:'— Principal —', value:''}, ...accounts.map(a=>({label: a.name, value: a.id}))]} />
          <_Input label="Descrição" name="description" value={form.description} onChange={handleChange} />
        </div>
      );
    }
    if (modalType === 'center') {
      return (
        <div style={{ display: 'grid', gap: 12 }}>
          <_Input label="Nome do Centro de Custo*" name="name" value={form.name} onChange={handleChange} />
        </div>
      );
    }
    if (modalType === 'bank') {
      return (
        <div style={{ display: 'grid', gap: 12 }}>
          <_Input label="Nome da Conta/Banco*" name="name" value={form.name} onChange={handleChange} />
          <_Input label="Tipo" name="account_type" type="select" value={form.account_type} onChange={handleChange} 
            options={[{label:'Conta Corrente',value:'checking'}, {label:'Poupança',value:'savings'}, {label:'Caixinha',value:'cash'}]} />
          {!current && (
            <_Input label="Saldo Inicial (R$)" name="initial_balance" type="currency" value={form.initial_balance} onChange={handleChange} />
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div style={{ paddingBottom: 32 }}>
      <CrudTable 
        title="Plano de Contas (Categorias)" icon="sitemap" loading={loading} data={accounts}
        columns={[
          { label: 'Nome', key: 'name', render: r => <strong>{r.name}</strong> },
          { label: 'Pai', key: 'parent_id', render: r => r.parent_id ? (accounts.find(a=>a.id===r.parent_id) || {}).name : '—' }
        ]}
        onCreate={() => { setForm({name:'', parent_id:'', description:''}); setCurrent(null); setModalType('account'); }}
        onEdit={(r) => { setForm({...r, parent_id: r.parent_id||''}); setCurrent(r); setModalType('account'); }}
        onDelete={(r) => setDelTarget({type:'account', id:r.id, name:r.name})}
      />

      <CrudTable 
        title="Centros de Custo" icon="store-alt" loading={loading} data={centers}
        columns={[
          { label: 'Nome', key: 'name', render: r => <strong>{r.name}</strong> }
        ]}
        onCreate={() => { setForm({name:''}); setCurrent(null); setModalType('center'); }}
        onEdit={(r) => { setForm({...r}); setCurrent(r); setModalType('center'); }}
        onDelete={(r) => setDelTarget({type:'center', id:r.id, name:r.name})}
      />

      <CrudTable 
        title="Contas Bancárias e Caixas" icon="university" loading={loading} data={banks}
        columns={[
          { label: 'Nome da Conta', key: 'name', render: r => <strong>{r.name}</strong> },
          { label: 'Tipo', key: 'account_type' },
          { label: 'Saldo Atual', key: 'current_balance', render: r => `R$ ${parseFloat(r.current_balance||0).toFixed(2).replace('.',',')}` }
        ]}
        onCreate={() => { setForm({name:'', account_type:'checking', initial_balance:0}); setCurrent(null); setModalType('bank'); }}
        onEdit={(r) => { setForm({...r}); setCurrent(r); setModalType('bank'); }}
        onDelete={(r) => setDelTarget({type:'bank', id:r.id, name:r.name})}
      />

      {/* MODAL CRUD */}
      <Overlay show={!!modalType} onClose={()=>setModalType(null)} 
        title={`${current ? 'Editar' : 'Novo'} Cadastro`} width={500}
        footer={
          <React.Fragment>
            <Btn outline color={C.soft} onClick={()=>setModalType(null)}>Cancelar</Btn>
            <Btn color={C.green} onClick={handleSave} disabled={saving}>{saving?'Salvando...':'Salvar'}</Btn>
          </React.Fragment>
        }
      >
        <form onSubmit={handleSave}>
          {renderForm()}
        </form>
      </Overlay>

      {/* MODAL DELETE */}
      <Overlay show={!!delTarget} onClose={()=>setDelTarget(null)} title="Confirmar Exclusão" width={400}
        footer={
          <React.Fragment>
            <Btn outline color={C.soft} onClick={()=>setDelTarget(null)}>Cancelar</Btn>
            <Btn color={C.red} onClick={handleDeleteConfirm} disabled={saving}>{saving?'Excluindo...':'Confirmar'}</Btn>
          </React.Fragment>
        }
      >
        <p style={{ color: C.text, fontSize: 14 }}>
          Deseja realmente excluir <strong>{delTarget && delTarget.name}</strong>?
          <br/><span style={{ fontSize: 12, color: C.soft }}>Esta ação não pode ser desfeita.</span>
        </p>
      </Overlay>
    </div>
  );
}



// WEBPACK FOOTER //
// ./src/views/AppViews/Financial/Settings/index.jsx